import {
  Component,
  Input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AysFabCreationStrategy } from '@bindoc-projects/virtual-clinic-domain-lib';
import { RadioButtonModule } from 'primeng/radiobutton';
import { FormsModule } from '@angular/forms';

interface FabCreationStrategyOption {
  value: AysFabCreationStrategy;
  inputId: string;
  label: string;
  description: string;
}

/** Which department mapping to use once the data carries a Fab-intern column. */
@Component({
  selector:
    'kost-data-import-de-new-dataset-input-fab-creation-strategy-select',
  template: `
    <fieldset class="fab-strategy">
      <legend class="fab-strategy__title">Fachabteilungs-Zuordnung</legend>
      <p class="fab-strategy__hint">
        In den Daten wurde die Spalte „Fab-intern“ erkannt. Wählen Sie, nach
        welcher Zuordnung die Fachabteilungen angelegt werden.
      </p>

      <div class="fab-strategy__options" role="radiogroup">
        @for (option of options; track option.value) {
          <label
            class="fab-strategy__option"
            [class.fab-strategy__option--selected]="
              fabCreationStrategy === option.value
            "
            [for]="option.inputId"
          >
            <p-radiobutton
              name="fabCreationStrategy"
              [value]="option.value"
              [ngModel]="fabCreationStrategy"
              (ngModelChange)="onFabCreationStrategyChange($event)"
              [inputId]="option.inputId"
            />
            <span class="flex flex-col gap-1">
              <span class="fab-strategy__label">{{ option.label }}</span>
              <span class="fab-strategy__description">{{
                option.description
              }}</span>
            </span>
          </label>
        }
      </div>
    </fieldset>
  `,
  styles: `
    :host {
      display: block;
    }
    .fab-strategy {
      margin: 0;
      padding: 0;
      border: 0;
      color: var(--p-text-color);
    }
    .fab-strategy__title {
      float: left;
      width: 100%;
      padding: 0;
      font-weight: 600;
    }
    .fab-strategy__hint {
      clear: both;
      margin: 0.25rem 0 1rem;
      font-size: 0.875rem;
      color: var(--p-text-muted-color);
    }
    .fab-strategy__options {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
      gap: 0.75rem;
    }
    .fab-strategy__option {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      padding: 0.75rem 1rem;
      border: 1px solid var(--p-content-border-color);
      border-radius: 4px;
      cursor: pointer;
    }
    .fab-strategy__option:hover {
      background: var(--p-content-hover-background);
    }
    .fab-strategy__option--selected {
      border-color: var(--p-primary-color);
    }
    .fab-strategy__label {
      font-weight: 500;
    }
    .fab-strategy__description {
      font-size: 0.75rem;
      color: var(--p-text-muted-color);
    }
  `,
  imports: [RadioButtonModule, FormsModule],

  changeDetection: ChangeDetectionStrategy.Eager,
})
export class KostDataImportDeNewDatasetInputFabCreationStrategySelectComponent {
  @Input()
  public fabCreationStrategy: AysFabCreationStrategy;

  public readonly fabCreationStrategyChange = output<AysFabCreationStrategy>();

  public readonly options: readonly FabCreationStrategyOption[] = [
    {
      value: 'DEFAULT_301',
      inputId: 'default-301',
      label: '§301 Fachabteilungszuordnung (Standard)',
      description: 'Fachabteilungen nach dem §301-Fachabteilungsschlüssel.',
    },
    {
      value: 'FAB_INTERN',
      inputId: 'fab-intern',
      label: 'Interne Fachabteilungszuordnung',
      description: 'Fachabteilungen nach der Spalte „Fab-intern“ (§21 Plus).',
    },
  ];

  public onFabCreationStrategyChange(strategy: AysFabCreationStrategy) {
    this.fabCreationStrategyChange.emit(strategy);
  }
}
